// 60 s stroke-sensor diagnostic recorder. Standalone: its own devicemotion +
// GPS listeners, mirrors useStrokeDetection's maths (phone mode) but never
// feeds the live stroke rate. Output = one JSON file for offline analysis.
const SAMPLE_RATE = 60;
const BUFFER_SIZE = SAMPLE_RATE * 4;
const MIN_PERIOD = 60 / 85;
const MAX_PERIOD = 60 / 5;
const LOW_HZ = 0.08;
const HIGH_HZ_PHONE = 0.9;

// One recording at a time; its state is broadcast so the cockpit can show the
// 'rowing detected' light only while a log is being recorded.
let active = null;
export const getActiveSensorLog = () => active;
// Latest on-water power inputs from the cockpit (null when the cockpit is not mounted).
let powerSample = null;
export const setLogPowerSample = (p) => { powerSample = p; };
const broadcast = (log = null) => window.dispatchEvent(new CustomEvent('crewz-sensor-log-state', { detail: { recording: !!active, log } }));

function makeBandPass(low, high, fs) {
  const dt = 1 / fs;
  const aLow = dt / (1 / (2 * Math.PI * high) + dt);
  const rcH = 1 / (2 * Math.PI * low);
  const aHigh = rcH / (rcH + dt);
  let lp = 0; let hp = 0; let prev = 0;
  return (x) => { lp += aLow * (x - lp); hp = aHigh * (hp + lp - prev); prev = lp; return hp; };
}

export function autocorrPeaks(signal) {
  const n = signal.length;
  if (n < 20) return { firstPeak: null, firstPeakLag: null, maxPeak: null };
  const mean = signal.reduce((a, b) => a + b, 0) / n;
  const x = signal.map((v) => v - mean);
  const variance = x.reduce((s, v) => s + v * v, 0);
  if (!variance) return { firstPeak: null, firstPeakLag: null, maxPeak: null };
  const maxLag = Math.min(n - 1, Math.floor(MAX_PERIOD * SAMPLE_RATE));
  const ac = [];
  for (let lag = 0; lag <= maxLag; lag += 1) {
    let s = 0;
    for (let i = 0; i < n - lag; i += 1) s += x[i] * x[i + lag];
    ac.push(s / variance);
  }
  const minLag = Math.floor(MIN_PERIOD * SAMPLE_RATE);
  let firstPeak = null; let firstPeakLag = null; let maxPeak = null;
  for (let i = Math.max(1, minLag); i < ac.length - 1; i += 1) {
    if (ac[i] > ac[i - 1] && ac[i] > ac[i + 1]) {
      if (maxPeak == null || ac[i] > maxPeak) maxPeak = ac[i];
      if (firstPeak == null && ac[i] > 0.15) { firstPeak = ac[i]; firstPeakLag = i; }
    }
  }
  return { firstPeak, firstPeakLag, maxPeak };
}

const stats = (arr) => {
  const v = arr.filter((x) => typeof x === 'number' && Number.isFinite(x));
  if (!v.length) return null;
  const r = (x) => Math.round(x * 1000) / 1000;
  return { min: r(Math.min(...v)), avg: r(v.reduce((a, b) => a + b, 0) / v.length), max: r(Math.max(...v)), n: v.length };
};

export function createStrokeSensorRecorder({ label, durationS = 60, getLiveRate, onTick, onDone }) {
  const t0 = Date.now();
  const raw = []; const gps = []; const updates = [];
  const acc = []; const filt = [];
  const bp = makeBandPass(LOW_HZ, HIGH_HZ_PHONE, SAMPLE_RATE);
  const sources = { acceleration: 0, includingGravity: 0 };
  let lastGps = null; let lastUpdate = 0; let watchId = null; let finished = false;
  const landscape = () => window.innerWidth > window.innerHeight;
  // Live rowing detector (runs only while a cockpit session is active).
  let det = null;
  const onDetector = (e) => { det = e.detail || null; };
  const REASON = { ac: 'AC', p2p: 'peak-to-peak', gps: 'GPS', 'p2p-close': 'peak-to-peak', 'gps-close': 'GPS', confirming: 'confirming' };
  const r3 = (v) => (typeof v === 'number' && Number.isFinite(v) ? +v.toFixed(3) : null);
  const detectorColumns = (now) => {
    const d = det && now - det.t <= 1500 ? det : null;
    if (!d) return { detectorState: null, rateShownSpm: null, detectorAcPeak: null, detectorP2p: null, detectorGpsSpeed: null, detectorGpsAccuracy: null, detectorClosedBy: null };
    const shown = d.rate > 0;
    return {
      detectorState: d.open ? 'open' : 'closed',
      rateShownSpm: shown ? d.rate : null,
      detectorAcPeak: r3(d.acPeak),
      detectorP2p: r3(d.p2p),
      detectorGpsSpeed: r3(d.gpsSpeed),
      detectorGpsAccuracy: r3(d.gpsAccuracy),
      detectorClosedBy: shown ? null : [...new Set((d.reasons || []).map((x) => REASON[x] || x))].join('+') || null,
    };
  };

  const onMotion = (e) => {
    let a = e.acceleration; let source = 'acceleration';
    if (!a || a.x === null) { a = e.accelerationIncludingGravity; source = 'includingGravity'; }
    if (!a) return;
    const now = Date.now();
    const ax = a.x || 0; const ay = a.y || 0; const az = a.z || 0;
    const land = landscape();
    const fwd = land ? ax : ay;
    sources[source] += 1;
    raw.push([now - t0, +ax.toFixed(4), +ay.toFixed(4), +az.toFixed(4), source === 'acceleration' ? 0 : 1, land ? 1 : 0, +fwd.toFixed(4)]);
    acc.push(fwd); filt.push(bp(fwd));
    if (acc.length > BUFFER_SIZE) { acc.shift(); filt.shift(); }
    if (now - lastUpdate >= 500 && acc.length >= SAMPLE_RATE * 2) {
      lastUpdate = now;
      const last1s = acc.slice(-SAMPLE_RATE);
      const mean1s = last1s.reduce((s, v) => s + v, 0) / last1s.length;
      const { firstPeak, firstPeakLag, maxPeak } = autocorrPeaks(acc);
      const gpsAge = lastGps ? (now - lastGps.t) / 1000 : null;
      updates.push({
        t: (now - t0) / 1000,
        source,
        avgMagnitude: +(last1s.reduce((s, v) => s + Math.abs(v), 0) / last1s.length).toFixed(3),
        avgMagnitudeMeanRemoved: +(last1s.reduce((s, v) => s + Math.abs(v - mean1s), 0) / last1s.length).toFixed(3),
        acFirstPeak: firstPeak == null ? null : +firstPeak.toFixed(3),
        acRateSpm: firstPeakLag ? Math.round((60 / (firstPeakLag / SAMPLE_RATE)) * 2) / 2 : null,
        acMaxPeak: maxPeak == null ? null : +maxPeak.toFixed(3),
        peakToPeak4s: +(Math.max(...filt) - Math.min(...filt)).toFixed(3),
        gpsSpeed: lastGps?.speed ?? null,
        gpsAccuracy: lastGps?.accuracy ?? null,
        gpsAgeS: gpsAge == null ? null : +gpsAge.toFixed(1),
        liveRateSpm: getLiveRate?.() ?? null,
        ...detectorColumns(now),
        boatSpeedForPowerMs: r3(powerSample?.speed),
        massKg: r3(powerSample?.mass),
        pBoatW: r3(powerSample?.pBoat),
        pwrShownW: r3(powerSample?.pwrShown),
      });
      const tick = { elapsed: (now - t0) / 1000, last: updates[updates.length - 1] };
      onTick?.(tick);
      window.dispatchEvent(new CustomEvent('crewz-sensor-log-tick', { detail: tick }));
    }
  };

  const stop = () => {
    if (finished) return null;
    finished = true;
    active = null;
    window.removeEventListener('devicemotion', onMotion);
    window.removeEventListener('crewz-rowing-detector', onDetector);
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    const summary = {
      avgMagnitude: stats(updates.map((u) => u.avgMagnitude)),
      avgMagnitudeMeanRemoved: stats(updates.map((u) => u.avgMagnitudeMeanRemoved)),
      acFirstPeak: stats(updates.map((u) => u.acFirstPeak)),
      acMaxPeak: stats(updates.map((u) => u.acMaxPeak)),
      peakToPeak4s: stats(updates.map((u) => u.peakToPeak4s)),
      gpsSpeed: stats(updates.map((u) => u.gpsSpeed)),
      liveRateSpm: stats(updates.map((u) => u.liveRateSpm)),
      accelerationSource: sources,
      motionSampleRateHz: raw.length ? +(raw.length / ((raw[raw.length - 1][0] || 1) / 1000)).toFixed(1) : 0,
    };
    const log = {
      kind: 'crewz-stroke-sensor-log', version: 1, label,
      startedAt: new Date(t0).toISOString(), durationS: (Date.now() - t0) / 1000,
      userAgent: navigator.userAgent, summary, updates, gps,
      rawColumns: ['t_ms', 'ax', 'ay', 'az', 'withGravity', 'landscape', 'forward'], raw,
      rowingDetectorColumns: 'detectorState open|closed; rateShownSpm (blank = no rate on screen); detectorAcPeak; detectorP2p (m/s2, 8 s filtered); detectorGpsSpeed / detectorGpsAccuracy (last fix the detector accepted, accuracy <= 10 m); detectorClosedBy when no rate shown: AC | peak-to-peak | GPS | confirming. All blank when no cockpit session is running.',
      powerColumns: 'boatSpeedForPowerMs (m/s used for on-water power); massKg (m); pBoatW (P_boat = 2.8 x v^3 x m^(1/3)); pwrShownW (PWR on screen, per rower, smoothed). Blank when the cockpit is not open.',
    };
    broadcast(log);
    onDone?.(log);
    return log;
  };

  window.addEventListener('devicemotion', onMotion);
  window.addEventListener('crewz-rowing-detector', onDetector);
  if (navigator.geolocation) {
    watchId = navigator.geolocation.watchPosition((p) => {
      lastGps = { t: Date.now(), speed: p.coords.speed, accuracy: p.coords.accuracy };
      gps.push({ t_ms: Date.now() - t0, lat: p.coords.latitude, lng: p.coords.longitude, speed: p.coords.speed, accuracy: p.coords.accuracy });
    }, () => {}, { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 });
  }
  const timer = durationS ? setTimeout(stop, durationS * 1000) : null; // null = whole session
  active = { stop: () => { clearTimeout(timer); return stop(); } };
  broadcast();
  return active;
}

export function downloadLog(log) {
  const blob = new Blob([JSON.stringify(log)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `crewz-stroke-log-${log.label.replace(/\W+/g, '-')}-${log.startedAt.slice(0, 19).replace(/[:T]/g, '')}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
